import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

// PLACEHOLDER: replace all posts with real blog content and CMS integration
const posts = [
  {
    id: 1,
    slug: 'introducing-digian-ai-voice-agent',
    category: 'Product',
    title: 'Introducing Digian AI Voice Agent: Intelligent Voice Automation for Enterprise',
    excerpt: 'Transform your IT support and customer service with natural language voice automation that integrates seamlessly with your existing systems.',
    readTime: '6 min read',
    date: 'March 2026',
    featured: true,
    author: 'Digian.ai Team',
  },
  {
    id: 2,
    slug: 'analytics-chat-agents-conversational-bi',
    category: 'Product',
    title: 'Analytics Chat Agents: Ask Questions, Get Insights Instantly',
    excerpt: 'Turn your data warehouse into a conversational interface. Query multiple sources, respect governance, and get answers in seconds.',
    readTime: '5 min read',
    date: 'March 2026',
    featured: true,
    author: 'Digian.ai Team',
  },
  {
    id: 3,
    slug: 'ai-customer-care-platform-overview',
    category: 'Product',
    title: 'Digian AI Customer Care: Automated Support at Enterprise Scale',
    excerpt: 'Deliver exceptional customer experiences with AI-powered omnichannel support that learns from every interaction.',
    readTime: '7 min read',
    date: 'February 2026',
    featured: false,
    author: 'Digian.ai Team',
  },
  {
    id: 4,
    slug: 'ai-web-chat-integration-guide',
    category: 'Product',
    title: 'AI Web Chat Integration: Branded Support for Customers & Employees',
    excerpt: 'Deploy intelligent web chat that matches your brand, integrates with your systems, and provides instant, accurate responses.',
    readTime: '5 min read',
    date: 'February 2026',
    featured: false,
    author: 'Digian.ai Team',
  },
  {
    id: 5,
    slug: 'ai-workflow-automation-end-to-end',
    category: 'Product',
    title: 'AI Workflow Automation: End-to-End Execution for IT & Customer Care',
    excerpt: 'Connect AI agents with your business systems to automate complete workflows from request to resolution.',
    readTime: '6 min read',
    date: 'January 2026',
    featured: false,
    author: 'Digian.ai Team',
  },
  {
    id: 6,
    slug: 'ai-compliance-studio-governance',
    category: 'Product',
    title: 'AI Compliance Studio: Governance, Audit Trails & QA for AI Support',
    excerpt: 'Ensure your AI agents meet compliance requirements with comprehensive audit logging, quality assurance, and governance controls.',
    readTime: '6 min read',
    date: 'January 2026',
    featured: false,
    author: 'Digian.ai Team',
  },
];

const categories = ['All', 'Product'];

const categoryColors = {
  Product: 'bg-indigo-100 text-indigo-700',
  'Data & Analytics': 'bg-emerald-100 text-emerald-700',
  Enterprise: 'bg-violet-100 text-violet-700',
  Security: 'bg-red-100 text-red-700',
  Migration: 'bg-amber-100 text-amber-700',
  AI: 'bg-cyan-100 text-cyan-700',
};

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All' ? posts : posts.filter(p => p.category === activeCategory);
  const featured = filtered.filter(p => p.featured);
  const rest = filtered.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="blog" />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 max-w-2xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600">Insights</p>
            <h1 className="text-4xl font-bold text-slate-900">Blog & Insights</h1>
            <p className="text-lg text-slate-600">
              Practical guides, product updates, and data leadership perspectives from the Digian.ai team.
            </p>
            {/* PLACEHOLDER: wire up to real CMS (e.g. Contentful, Sanity, Hashnode) */}
          </motion.div>
        </div>
      </section>

      {/* Category filter */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCategory === cat
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">

        {/* No posts message */}
        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="text-slate-500 text-lg">No posts found in this category yet.</p>
            <p className="text-slate-400 text-sm mt-2">Check back soon for new content!</p>
          </div>
        )}

        {/* Featured */}
        {featured.length > 0 && (
          <div className="mb-16">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Featured</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featured.map((post, i) => (
                <motion.div key={post.id} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-2xl border border-indigo-100 p-8 hover:border-indigo-300 transition-all group">
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[post.category] || 'bg-slate-100 text-slate-600'}`}>
                      {post.category}
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-indigo-700 transition-colors leading-snug">
                    {post.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">{post.date} · {post.author}</span>
                    <Link to={`/blog/${post.slug}`} className="text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                      Read more <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* All posts */}
        {rest.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-6">All Posts</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post, i) => (
                <motion.div key={post.id} {...fadeUp} transition={{ delay: i * 0.07 }}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:shadow-sm transition-all group">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${categoryColors[post.category] || 'bg-slate-100 text-slate-600'}`}>
                      {post.category}
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-indigo-700 transition-colors leading-snug">
                    {post.title}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">{post.date}</span>
                    <Link to={`/blog/${post.slug}`} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                      Read <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
